import { ArrowUpRight } from '@phosphor-icons/react';
import type {
  AboutValues,
  ContactValues,
  EducationValues,
  ExperienceValues,
  HeroValues,
  ProjectsValues,
} from '../../schemas';
import { PLATFORM_META } from '../../linkPlatforms';
import type { TemplateProps } from './shared';

export function MinimalTemplate({ draft, enabled, ownerName }: TemplateProps) {
  return (
    <div className="min-h-full bg-canvas font-sans text-ink">
      <div className="mx-auto max-w-3xl px-8 py-12">
        <header className="flex items-center justify-between border-b border-line pb-5 text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          <span className="font-mono">{ownerName}</span>
          <span className="font-mono">{new Date().getFullYear()}</span>
        </header>

        {enabled.has('hero') && draft.hero ? <HeroBlock values={draft.hero} /> : null}
        {enabled.has('about') && draft.about ? <AboutBlock values={draft.about} /> : null}
        {enabled.has('projects') && draft.projects ? <ProjectsBlock values={draft.projects} /> : null}
        {enabled.has('experience') && draft.experience ? <ExperienceBlock values={draft.experience} /> : null}
        {enabled.has('education') && draft.education ? <EducationBlock values={draft.education} /> : null}
        {enabled.has('contact') && draft.contact ? <ContactBlock values={draft.contact} /> : null}

        <footer className="mt-16 border-t border-line pt-5 text-center font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          Built with Portfoli
        </footer>
      </div>
    </div>
  );
}

function SectionHeading({ kicker }: { kicker: string }) {
  return (
    <div className="mt-14 mb-5">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">{kicker}</p>
    </div>
  );
}

function HeroBlock({ values }: { values: HeroValues }) {
  const availabilityLabel: Record<HeroValues['availability'], string> = {
    available: 'Available for work',
    limited: 'Selective inquiries',
    closed: 'Not taking work',
  };
  return (
    <section className="pt-10">
      {values.portraitUrl ? (
        <img
          src={values.portraitUrl}
          alt=""
          className="mb-6 h-20 w-20 rounded-2xl object-cover"
        />
      ) : null}
      {values.tagline ? (
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
          {values.tagline}
        </p>
      ) : null}
      <h1 className="mt-3 font-display text-4xl font-semibold leading-[1.1] tracking-tight">
        {values.headline}
      </h1>
      {values.subheadline ? (
        <p className="mt-4 max-w-[52ch] text-base leading-relaxed text-ink-muted">
          {values.subheadline}
        </p>
      ) : null}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
            values.availability === 'available'
              ? 'border-green-600/30 bg-green-50 text-green-900'
              : values.availability === 'limited'
                ? 'border-amber-600/30 bg-amber-50 text-amber-900'
                : 'border-line bg-rail text-ink-muted'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              values.availability === 'available'
                ? 'bg-green-600'
                : values.availability === 'limited'
                  ? 'bg-amber-600'
                  : 'bg-ink-faint'
            }`}
          />
          {availabilityLabel[values.availability]}
        </span>
        {values.ctaLabel && values.ctaHref ? (
          <a
            href={values.ctaHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-1.5 text-sm font-medium text-board"
          >
            {values.ctaLabel}
            <ArrowUpRight size={12} weight="bold" />
          </a>
        ) : null}
      </div>
    </section>
  );
}

function AboutBlock({ values }: { values: AboutValues }) {
  return (
    <section>
      <SectionHeading kicker="01 · About" />
      <p className="max-w-[60ch] text-base leading-relaxed text-ink">{values.body}</p>
      {values.skills.length ? (
        <div className="mt-5 flex flex-wrap gap-2">
          {values.skills.map((s) => (
            <span
              key={s}
              className="inline-flex items-center rounded-full border border-line bg-board px-2.5 py-1 text-xs text-ink"
            >
              {s}
            </span>
          ))}
        </div>
      ) : null}
      {values.resumeUrl ? (
        <a
          href={values.resumeUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-ink underline underline-offset-4"
        >
          Download résumé <ArrowUpRight size={12} weight="bold" />
        </a>
      ) : null}
    </section>
  );
}

function ProjectsBlock({ values }: { values: ProjectsValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionHeading kicker="02 · Selected work" />
      <div className="space-y-10">
        {values.items.map((p) => (
          <article key={p.id} className="space-y-3">
            {p.mediaKind !== 'none' && p.mediaUrl ? (
              <div className="overflow-hidden rounded-lg border border-line bg-board">
                {p.mediaKind === 'image' ? (
                  <img
                    src={p.mediaUrl}
                    alt={p.mediaAlt ?? p.title}
                    className="aspect-[16/10] w-full object-cover"
                  />
                ) : (
                  <div className="aspect-[16/10] w-full bg-rail text-center text-xs text-ink-faint grid place-items-center">
                    Video preview · {p.mediaUrl}
                  </div>
                )}
              </div>
            ) : null}
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-display text-xl font-semibold tracking-tight">{p.title}</h3>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                {p.year}
              </span>
            </div>
            {(p.role || p.client) && (
              <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                {[p.role, p.client].filter(Boolean).join(' · ')}
              </p>
            )}
            {p.summary ? (
              <p className="max-w-[60ch] text-sm leading-relaxed text-ink-muted">{p.summary}</p>
            ) : null}
            {p.bullets && p.bullets.length ? (
              <ul className="space-y-1.5 text-sm text-ink-muted">
                {p.bullets.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-ink-faint">—</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {p.tags && p.tags.length ? (
              <div className="flex flex-wrap gap-1.5">
                {p.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-line bg-board px-2 py-0.5 text-[11px] text-ink-muted"
                  >
                    {t}
                  </span>
                ))}
              </div>
            ) : null}
            {p.url ? (
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-ink hover:underline"
              >
                View project <ArrowUpRight size={12} weight="bold" />
              </a>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function ExperienceBlock({ values }: { values: ExperienceValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionHeading kicker="03 · Experience" />
      <div className="space-y-7">
        {values.items.map((e) => (
          <div key={e.id} className="border-t border-line pt-5 first:border-t-0 first:pt-0">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-display text-lg font-semibold tracking-tight">{e.role}</h3>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                {e.start} – {e.current ? 'Present' : e.end}
              </span>
            </div>
            <p className="mt-0.5 text-sm text-ink-muted">
              {e.company}
              {e.location ? ` · ${e.location}` : ''}
            </p>
            {e.summary ? (
              <p className="mt-2 max-w-[60ch] text-sm leading-relaxed text-ink">{e.summary}</p>
            ) : null}
            {e.highlights && e.highlights.length ? (
              <ul className="mt-3 space-y-1.5 text-sm text-ink-muted">
                {e.highlights.map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-ink-faint">—</span>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function EducationBlock({ values }: { values: EducationValues }) {
  if (!values.items.length) return null;
  return (
    <section>
      <SectionHeading kicker="04 · Education" />
      <div className="space-y-5">
        {values.items.map((e) => (
          <div key={e.id}>
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-display text-lg font-semibold tracking-tight">{e.institution}</h3>
              <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-ink-faint">
                {e.startYear}{e.endYear ? ` – ${e.endYear}` : ''}
              </span>
            </div>
            <p className="mt-0.5 text-sm text-ink-muted">
              {e.credential}
              {e.gpa ? ` · GPA ${e.gpa}` : ''}
            </p>
            {e.note ? (
              <p className="mt-1.5 max-w-[60ch] text-sm text-ink-muted">{e.note}</p>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactBlock({ values }: { values: ContactValues }) {
  return (
    <section>
      <SectionHeading kicker="05 · Contact" />
      <div className="space-y-4">
        {values.email ? (
          <p className="text-sm text-ink">
            <a href={`mailto:${values.email}`} className="underline underline-offset-4">
              {values.email}
            </a>
          </p>
        ) : null}
        {values.phone ? (
          <p className="text-sm text-ink">
            <a href={`tel:${values.phone.replace(/\s+/g, '')}`} className="underline underline-offset-4">
              {values.phone}
            </a>
          </p>
        ) : null}
        {values.location ? <p className="text-sm text-ink-muted">{values.location}</p> : null}
        {values.links.length ? (
          <ul className="flex flex-wrap gap-x-4 gap-y-2">
            {values.links.map((l) => {
              const meta = PLATFORM_META[l.platform] ?? PLATFORM_META.custom;
              const Icon = meta.icon;
              const href = l.platform === 'email' && !l.href.startsWith('mailto:') ? `mailto:${l.href}` : l.href;
              return (
                <li key={l.id}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-ink hover:underline"
                  >
                    <Icon size={14} weight="regular" className="text-ink-muted" />
                    {l.label || meta.label}
                  </a>
                </li>
              );
            })}
          </ul>
        ) : null}
        {values.allowInquiryForm ? (
          <div className="mt-4 rounded-xl border border-dashed border-line bg-board p-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-faint">
              Send an inquiry
            </p>
            <p className="mt-2 text-sm text-ink-muted">
              A short form renders here on your live site.
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
